import React, { useEffect, useState, useImperativeHandle, useRef } from "react";
import { isEqual } from "lodash";
import SubtitleRow from "./SubtitleRow";
import { useFieldArray, useForm } from "react-hook-form";
import styled from "styled-components";
import ReactPaginate from "react-paginate";

const Tips = styled.div`
    color: rgba(255, 255, 255, 0.8);
    border-radius: 4px;
    background-color: rgba(255, 255, 255, 0.1);
    font-size: 12px;
    padding: 8px;
    margin-bottom: 8px;
`;

const EditorContainer = styled.div`
    width: 600px;
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const SubtitleRowsContainer = styled.div`
    overflow-y: auto;
    max-height: 550px; /* Increased from 400px to 550px */
    margin-bottom: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding-bottom: 8px;
`;

const PaginationContainer = styled.div`
    margin-top: 16px;
    margin-bottom: 16px;
    width: 100%;
    display: block;
    position: sticky;
    bottom: 0;
    z-index: 10;
    padding: 8px 0; /* Reduced from 10px to 8px */
    border-radius: 4px;
    
    .pagination {
        display: flex;
        justify-content: center;
        list-style-type: none;
        padding: 0;
        margin: 6px 0; /* Reduced from 10px to 6px */
        
        li {
            margin: 0 3px; /* Reduced from 5px to 3px */
            display: inline-block;
            
            a {
                padding: 6px 10px; /* Reduced from 8px 12px to 6px 10px */
                border-radius: 4px;
                cursor: pointer;
                color: var(--gray-800);
                background-color: var(--gray-200);
                transition: all 0.3s ease;
                display: inline-block;
                min-width: 28px; /* Reduced from 32px to 28px */
                text-align: center;
                border: 1px solid var(--gray-300);
                font-weight: 500;
                font-size: 0.9rem; /* Added slightly smaller font size */
                box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
                
                &:hover {
                    background-color: var(--gray-300);
                }
            }
            
            &.active a {
                background-color: var(--primary-main);
                color: white;
                border-color: var(--primary-main);
                font-weight: bold;
            }
            
            &.disabled a {
                color: var(--gray-400);
                pointer-events: none;
                background-color: var(--gray-100);
                border-color: var(--gray-200);
            }
        }
    }
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    
    @media (max-width: 640px) {
        flex-direction: column-reverse;
    }
`;

const ItemsPerPageSelector = styled.div`
    display: flex;
    align-items: center;
    font-size: 14px;
    color: var(--gray-700);
    
    select {
        margin-left: 8px;
        padding: 4px 8px;
        border-radius: 4px;
        border: 1px solid var(--gray-300);
        background-color: var(--gray-200);
        cursor: pointer;
        font-size: 14px;
        
        &:focus {
            outline: none;
            border-color: var(--primary-main);
            box-shadow: 0 0 0 2px rgba(var(--primary-main-rgb), 0.2);
        }
    }
`;

const defaultValue = {
    id: "",
    startTime: null,
    endTime: null,
    pauseOnExit: false,
    direction: "horizontal",
    snapToLines: true,
    linePosition: "auto",
    lineAlign: "start",
    textPosition: "auto",
    positionAlign: "auto",
    size: 100,
    alignment: "center",
    text: "",
    tree: null,
    startHours: null,
    startMinutes: null,
    startSeconds: null,
    startMilliseconds: null,
    endHours: null,
    endMinutes: null,
    endSeconds: null,
    endMilliseconds: null
};

const SubtitleEditor = (props, ref) => {
    const { list: orignalList, onSeekAndPlay, onPause, onReplay, currentTime, isPlaying, duration } = props;
    const [list, setList] = useState(orignalList || []);
    const subtitleRefs = useRef([]); // Array to store refs for each subtitle row
    const prevActiveIndex = useRef(-1); // Track previous active index
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(50); // Changed from constant to state
    const subtitleRowsContainerRef = useRef(null); // Ref for the scrollable container
    const prevPageRef = useRef(currentPage); // Ref to store the previous page index

    const formMethod = useForm({
        mode: "all",
        defaultValues: { list }
    });

    const { control, reset, handleSubmit, watch } = formMethod;

    // Effect to reset prevActiveIndex when currentPage changes.
    // This ensures that after any page change (pagination click or programmatic),
    // the main scrolling useEffect correctly identifies the first scroll event on the new page.
    // Also, scroll to top if navigating to a NEXT page.
    useEffect(() => {
        if (currentPage > prevPageRef.current && subtitleRowsContainerRef.current) {
            // Navigated to a NEXT page, scroll container to top
            subtitleRowsContainerRef.current.scrollTop = 0;
        }
        prevActiveIndex.current = -1; // Reset for any page change
        prevPageRef.current = currentPage; // Update the previous page ref for the next change
    }, [currentPage]);

    const { fields, update, insert, remove } = useFieldArray({
        control,
        name: "list"
    });

    // Calculate the items to display based on pagination
    const paginatedFields = fields.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
    );

    // Calculate total pages
    const pageCount = Math.ceil(fields.length / itemsPerPage);

    // Handle page change
    const handlePageChange = (selectedItem) => {
        setCurrentPage(selectedItem.selected);
        // prevActiveIndex.current and subtitleRefs.current are handled by
        // the new useEffect hook listening to currentPage and the render cycle respectively.
    };

    // Handle items per page change
    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value);
        // Calculate the first item index on current page
        const firstItemIndex = currentPage * itemsPerPage;
        // Calculate which page this item would be on with new items per page
        const newPage = Math.floor(firstItemIndex / newItemsPerPage);
        
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(newPage);
    };

    useImperativeHandle(ref, () => {
        return {
            save: handleSubmit((data) => props.onSubmit?.(data)),
            getCurrentData: () => formMethod.getValues().list
        };
    });

    useEffect(() => {
        reset({ list: orignalList });
    }, [orignalList, reset]);

    // Scroll to the active subtitle row and handle pagination change if needed
    useEffect(() => {
        // Ensure refs array matches the number of fields on current page
        subtitleRefs.current = subtitleRefs.current.slice(0, paginatedFields.length);

        const activeGlobalIndex = fields.findIndex((field, index) => {
            const row = watch(`list.${index}`);
            const startTime =
                (parseInt(row.startHours || 0) * 3600) +
                (parseInt(row.startMinutes || 0) * 60) +
                (parseInt(row.startSeconds || 0)) +
                (parseInt(row.startMilliseconds || 0) / 1000);
            const endTime =
                (parseInt(row.endHours || 0) * 3600) +
                (parseInt(row.endMinutes || 0) * 60) +
                (parseInt(row.endSeconds || 0)) +
                (parseInt(row.endMilliseconds || 0) / 1000);
            return currentTime >= startTime && currentTime < endTime;
        });

        if (!props.isPlaying || activeGlobalIndex === -1) return;

        // If active subtitle is outside current page, switch to its page
        const activePage = Math.floor(activeGlobalIndex / itemsPerPage);
        if (activePage !== currentPage) {
            setCurrentPage(activePage); // This will trigger the useEffect for currentPage, resetting prevActiveIndex
            return;
        }

        // Calculate the local index within the current page
        const activeLocalIndex = activeGlobalIndex - (currentPage * itemsPerPage);
        
        if (activeLocalIndex !== prevActiveIndex.current) {
            const currentItemRef = subtitleRefs.current[activeLocalIndex]?.current;
            
            // Scroll the current active item first.
            if (currentItemRef) {
                currentItemRef.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest"
                });
            }

            // Then scroll the next item (if it exists on the current page) after a short delay.
            // This helps ensure the DOM/layout is stable after a potential page change and first scroll.
            const nextItemLocalIndex = activeLocalIndex + 1;
            if (nextItemLocalIndex < paginatedFields.length) {
                setTimeout(() => {
                    // Re-access the ref inside the timeout to ensure it's the most current one,
                    // especially if rapid updates could occur.
                    const nextItemRefAfterTimeout = subtitleRefs.current[nextItemLocalIndex]?.current;
                    if (nextItemRefAfterTimeout) {
                        nextItemRefAfterTimeout.scrollIntoView({
                            behavior: "smooth",
                            block: "nearest"
                        });
                    }
                }, 50); // 50ms delay, can be adjusted.
            }
            
            prevActiveIndex.current = activeLocalIndex;
        }
    }, [currentTime, fields, watch, props.isPlaying, currentPage, paginatedFields.length, itemsPerPage]);

    const handleInsertAfter = (index) => {
        const globalIndex = index + (currentPage * itemsPerPage);
        const currentRow = watch(`list.${globalIndex}`);
        const newRow = {
            ...defaultValue,
            startHours: currentRow.startHours,
            startMinutes: currentRow.startMinutes,
            startSeconds: currentRow.startSeconds,
            startMilliseconds: currentRow.startMilliseconds,
            endHours: currentRow.endHours,
            endMinutes: currentRow.endMinutes,
            endSeconds: currentRow.endSeconds,
            endMilliseconds: currentRow.endMilliseconds,
            text: "" // New row starts with empty text
        };
        insert(globalIndex + 1, newRow);
        
        // If inserting at the end of the page, move to the next page
        if (index === itemsPerPage - 1) {
            setCurrentPage(currentPage + 1);
        }
        
        setTimeout(() => {
            const localIndex = (globalIndex + 1) - (currentPage * itemsPerPage);
            const input = document.getElementById(`subtitle-text_${localIndex}`);
            input?.focus();
        }, 150);
    };

    const handleAddNextRow = (index, dataArray) => {
        const globalIndex = index + (currentPage * itemsPerPage);
        const [startText, endText] = dataArray;
        update(globalIndex, {
            ...fields[globalIndex],
            text: startText
        });

        insert(globalIndex + 1, { ...defaultValue, text: endText });
        
        // If inserting at the end of the page, move to the next page
        if (index === itemsPerPage - 1) {
            setCurrentPage(currentPage + 1);
        }
        
        setTimeout(() => {
            const localIndex = (globalIndex + 1) - (currentPage * itemsPerPage);
            const input = document.getElementById(`subtitle-text_${localIndex}`);
            input?.focus();
        }, 150);
    };

    const handleAddRowBefore = (index, text) => {
        const globalIndex = index + (currentPage * itemsPerPage);
        insert(globalIndex, { ...defaultValue, text });
        setTimeout(() => {
            const input = document.getElementById(`subtitle-text_${index}`);
            input?.focus();
        }, 150);
    };

    const handleAddRowAfter = (index, text) => {
        const globalIndex = index + (currentPage * itemsPerPage);
        insert(globalIndex + 1, { ...defaultValue, text });
        
        // If inserting at the end of the page, move to the next page
        if (index === itemsPerPage - 1) {
            setCurrentPage(currentPage + 1);
        }
        
        setTimeout(() => {
            const localIndex = (globalIndex + 1) - (currentPage * itemsPerPage);
            const input = document.getElementById(`subtitle-text_${localIndex}`);
            input?.focus();
        }, 150);
    };

    const handleCombineBefore = (index) => {
        const globalIndex = index + (currentPage * itemsPerPage);
        if (globalIndex > 0) {
            const prevSubtitle = watch(`list.${globalIndex - 1}`);
            const currentSubTitle = watch(`list.${globalIndex}`);

            const startPosition = prevSubtitle.text.length;

            update(globalIndex - 1, {
                ...prevSubtitle,
                text: `${prevSubtitle.text}${currentSubTitle.text}`,
                startHours: prevSubtitle.startHours ?? currentSubTitle.startHours,
                startMinutes: prevSubtitle.startMinutes ?? currentSubTitle.startMinutes,
                startSeconds: prevSubtitle.startSeconds ?? currentSubTitle.startSeconds,
                startMilliseconds: prevSubtitle.startMilliseconds ?? currentSubTitle.startMilliseconds,
                endHours: currentSubTitle.endHours ?? prevSubtitle.endHours,
                endMinutes: currentSubTitle.endMinutes ?? prevSubtitle.endMinutes,
                endSeconds: currentSubTitle.endSeconds ?? prevSubtitle.endSeconds,
                endMilliseconds: currentSubTitle.endMilliseconds ?? prevSubtitle.endMilliseconds
            });
            remove(globalIndex);
            
            // If we're at the first item of a page and not on the first page, go back a page
            if (index === 0 && currentPage > 0) {
                setCurrentPage(currentPage - 1);
            }
            
            setTimeout(() => {
                const localPrevIndex = (globalIndex - 1) - (currentPage * itemsPerPage);
                const input = document.getElementById(`subtitle-text_${localPrevIndex}`);
                if (input) {
                    input.focus();
                    input.selectionStart = startPosition;
                }
            }, 150);
        }
    };

    const handleCombineAfter = (index) => {
        const globalIndex = index + (currentPage * itemsPerPage);
        if (globalIndex < fields.length - 1) {
            const currentSubTitle = watch(`list.${globalIndex}`);
            const nextSubtitle = watch(`list.${globalIndex + 1}`);

            const startPosition = currentSubTitle.text.length;

            update(globalIndex, {
                ...currentSubTitle,
                text: `${currentSubTitle.text}${nextSubtitle.text}`,
                startHours: currentSubTitle.startHours ?? nextSubtitle.startHours,
                startMinutes: currentSubTitle.startMinutes ?? nextSubtitle.startMinutes,
                startSeconds: currentSubTitle.startSeconds ?? nextSubtitle.startSeconds,
                startMilliseconds: currentSubTitle.startMilliseconds ?? nextSubtitle.startMilliseconds,
                endHours: nextSubtitle.endHours ?? currentSubTitle.endHours,
                endMinutes: nextSubtitle.endMinutes ?? currentSubTitle.endMinutes,
                endSeconds: nextSubtitle.endSeconds ?? currentSubTitle.endSeconds,
                endMilliseconds: nextSubtitle.endMilliseconds ?? currentSubTitle.endMilliseconds
            });

            remove(globalIndex + 1);

            setTimeout(() => {
                const input = document.getElementById(`subtitle-text_${index}`);
                if (input) {
                    input.focus();
                    input.selectionStart = startPosition;
                    const lineHeight = parseInt(window.getComputedStyle(input).lineHeight);
                    const linesAbove = Math.floor(startPosition / input.cols);
                    const scrollToY = linesAbove * lineHeight;
                    input.scrollTop = scrollToY;
                }
            }, 150);
        }
    };

    const handleRemove = (index) => {
        const globalIndex = index + (currentPage * itemsPerPage);
        remove(globalIndex);
        
        // If we remove the last item on a page and it's not the first page, go back a page
        if (index === 0 && paginatedFields.length === 1 && currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    useEffect(() => {
        if (!isEqual(list, props.list)) {
            setList(props.list || []);
        }
    }, [orignalList, props.list]);

    useEffect(() => {
        console.log(`Total fields: ${fields.length}, Items per page: ${itemsPerPage}, Pages: ${pageCount}`);
    }, [fields.length, itemsPerPage, pageCount]);

    return (
        <EditorContainer>
            <SubtitleRowsContainer ref={subtitleRowsContainerRef}>
                {paginatedFields.map((field, index) => {
                    // Initialize ref for each row if it doesn't exist
                    if (!subtitleRefs.current[index]) {
                        subtitleRefs.current[index] = React.createRef();
                    }
                    
                    // Calculate the global index
                    const globalIndex = index + (currentPage * itemsPerPage);
                    
                    return (
                        <div ref={subtitleRefs.current[index]} key={field.id}>
                            <SubtitleRow
                                id={field.id}
                                control={control}
                                data={field}
                                onNextRow={handleAddNextRow}
                                onAddRowBefore={handleAddRowBefore}
                                onAddRowAfter={handleAddRowAfter}
                                index={index}
                                globalIndex={globalIndex}
                                onCombineBefore={handleCombineBefore}
                                onCombineAfter={handleCombineAfter}
                                onRemove={handleRemove}
                                onSeekAndPlay={onSeekAndPlay}
                                onPause={onPause}
                                onReplay={onReplay}
                                isPlaying={isPlaying}
                                currentTime={currentTime}
                                onInsertAfter={handleInsertAfter}
                                duration={duration}
                            />
                        </div>
                    );
                })}
            </SubtitleRowsContainer>
            
            <PaginationContainer className="subtitle-pagination-container">
                <PaginationControls>
                    <ItemsPerPageSelector>
                        <label htmlFor="items-per-page">Items per page:</label>
                        <select 
                            id="items-per-page"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={40}>40</option>
                            <option value={50}>50</option>
                        </select>
                    </ItemsPerPageSelector>
                    
                    <ReactPaginate
                        previousLabel={"← Previous"}
                        nextLabel={"Next →"}
                        pageCount={Math.max(1, pageCount)}
                        onPageChange={handlePageChange}
                        containerClassName={"pagination"}
                        activeClassName={"active"}
                        forcePage={currentPage}
                        pageRangeDisplayed={3}
                        marginPagesDisplayed={1}
                    />
                </PaginationControls>
            </PaginationContainer>
        </EditorContainer>
    );
};

export default React.forwardRef(SubtitleEditor);
